<template>
  <div id="CreateClaimOrderLookup" class="bg-light px-3">
    <div class="col">
      <br />
      <h2>Claim Reference</h2>
      <p>Name this claim for future reference.</p>
      <br />

      <!-- Dealer Reference Number -->
      <div id="dealerReference" class="row no-gutters">
        <h3>Dealer Reference Number&nbsp;<span class="red-required">*&nbsp;&nbsp;</span></h3>
        <ToolTipPopOver :popover-message="dealerReferencePopOver" :popover-title="dealerReferencePopoverTitle" />
      </div>
      <div class="row no-gutters mb-4">
        <div class="col col-lg-4 col-md-4 col-sm-6 p-0">
          <b-form-group
            id="OrderLookup"
            class="input-group"
            label-for="dealerReferenceNumber"
            label-class="font-weight-bold required"
            aria-label="Dealer Reference Number"
            aria-describedby="OrderLookup"
          >
            <b-form-input
              id="dealerReferenceNumber"
              v-model="claimDto.CustomerReferenceNumber"
              v-validate="{ required: true, max: 35 }"
              type="text"
              name="CustomerReferenceNumber"
              aria-describedby="dealerReferenceLiveFeedback"
              :state="ValidateState('CustomerReferenceNumber')"
            />
            <b-form-invalid-feedback id="dealerReferenceLiveFeedback">
              Dealer Reference Number is Required
            </b-form-invalid-feedback>
            <b-form-valid-feedback />
          </b-form-group>
        </div>
      </div>

      <!-- Search -->
      <div v-if="claimDto.ClaimType === 'PartsIssue' || claimDto.ClaimType === 'FreightOnly'" class="searchArea">
        <h2>Search</h2>
        <div class="mb-5">
          Search by Invoice #, Order #, PO # or Part # to locate the part(s) you would like to claim
        </div>
        <div class="row no-gutters pb-5">
          <div class="col col-lg-4 col-md-9 col-xs-12">
            <label><strong>Search by Invoice, Order, PO #</strong></label>
            <div id="searchInvoiceOrders" class="input-group">
              <input
                id="searchInvoiceText"
                v-model="invoiceOrderNumber"
                type="text"
                class="form-control"
                aria-label="Search by Invoice, Order, PO#"
                aria-describedby="searchInvoiceOrders"
              />
              <div class="input-group-append">
                <DnFunctionButton
                  button-id="searchByInvoice"
                  :callback-fn="SearchByInvoiceOrderPo"
                  text="SEARCH"
                  :size="largeSize"
                  :loading="loadingSearchByInvoice"
                />
              </div>
            </div>
          </div>
          <div class="col-lg-2 col-md-9 text-center">
            <strong>OR</strong>
          </div>
          <div class="col col-lg-4 col-md-9 col-xs-12">
            <label><strong>Search by Part #</strong></label>
            <div id="searchPart" class="input-group">
              <input
                v-model="partNumber"
                type="text"
                class="form-control"
                aria-label="Search by Part #"
                aria-describedby="searchPart"
              />
              <div class="input-group-append">
                <DnFunctionButton
                  button-id="searchByPart"
                  :callback-fn="SearchByPartNumber"
                  text="SEARCH"
                  :size="largeSize"
                  :loading="loadingSearchByPartNumber"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Search Results -->
      <div v-if="ShowHideSearchResults" id="searchDataContainer">
        <!-- If iScala Invoice show message -->
        <b-alert v-show="iScalaInvoices.length > 0" variant="warning" show>
          {{ format(getContentItem("IscalaInvoiceClaimsMessage"), iScalaInvoices) }}
        </b-alert>
        <div v-if="ShowHideDataTable" class="row no-gutters">
          <h3 class="col-lg-12 col-sm-12">
            {{ getContentItem("newClaimsSearchResultsFound") }}&nbsp;"{{ searchText }}"
          </h3>
          <div class="col-lg-12, col-sm-12 mb-3">
            Select an invoice from the following list to locate the part(s) you would like to create a claim for
          </div>
          <b-alert v-show="claimAllowedDaysMessage !== ''" id="ClaimAllowedDaysMessage" variant="secondary" show>
            {{ claimAllowedDaysMessage }}
          </b-alert>
          <DnTable
            id="claimsSearchResults"
            hover
            striped
            :items="searchData"
            :fields="searchColumns"
            stacked="md"
            :no-provider-filtering="true"
            show-empty
            :no-local-sorting="true"
            class="blue mb-5"
          >
            <template #selectedRow="{ row }">
              <b-form-group>
                <b-form-radio-group
                  :id="'invoiceRow' + row.index"
                  :options="defaultRadioButtons"
                  plain
                  name="selectedInvoiceRow"
                  @change="SetSelectedRow(row.item)"
                />
              </b-form-group>
            </template>
            <template #invoiceDate="{ row }">
              <date-time :date-string="row.item.InvoiceDate" />
            </template>
            <template #total="{ row }">
              <currency-text :amount="row.item.Total" :currency-code="row.item.CurrencyCode" />
            </template>
          </DnTable>
        </div>
        <div v-else id="noResultsFound" class="row no-gutters pb-5">
          <h3>{{ getContentItem("newClaimsSearchResultsNotFound") }}&nbsp;"{{ searchText }}"</h3>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import ToolTipPopOver from "@/components/Elements/ToolTipPopover";
import { stringHelper } from "@/components/mixins/stringHelper";
import { ClaimsService } from "@/services/ClaimsService";
import CurrencyText from "@/components/Elements/CurrencyText";
import DnFunctionButton from "@/components/Elements/DnFunctionButton";
import DateTime from "@/components/Elements/DateTime";
import { sizeHelper } from "@/components/mixins/sizeHelper";
import { inputValidateState } from "@/components/mixins/inputValidateState";
import { ExclusionType } from "@/types";
import DnTable from "@/components/Elements/DnTable/DnTable.vue";

export default {
  components: {
    ToolTipPopOver,
    CurrencyText,
    DateTime,
    DnFunctionButton,
    DnTable,
  },

  mixins: [stringHelper, sizeHelper, inputValidateState],

  props: {
    validationState: {
      orderLookupTab: false,
    },
    claimDto: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    dealerReferencePopOver: "Dealers unique reference number for this claim.",
    dealerReferencePopoverTitle: "Dealer Reference Note",
    searchColumns: [
      { key: "selectedRow", label: "", sortable: false },
      { key: "InvoiceNumber", label: "Invoice Number", sortable: false },
      { key: "SalesOrg", label: "Sales Org.", sortable: false },
      { key: "ShipVia", label: "Ship Via", sortable: false },
      { key: "OrderNumber", label: "Order Number", sortable: false },
      { key: "PoNumber", label: "PO Number", sortable: false },
      { key: "invoiceDate", label: "Invoice Date", sortable: false },
      { key: "InvoicedItems", label: "Invoiced Items", sortable: false },
      { key: "total", label: "Total", sortable: false },
    ],
    searchData: Object,
    defaultRadioButtons: [{ text: "", value: undefined }],
    searchText: "",
    partNumber: "",
    invoiceOrderNumber: "",
    loadingSearchByInvoice: false,
    loadingSearchByPartNumber: false,
    claimAllowedDaysMessage: "",
    iScalaInvoices: [],
  }),

  computed: {
    ...mapGetters(["activeAccountId", "user", "getContentItem", "hasPermission"]),
    ShowHideSearchResults: function () {
      return this.searchData !== Object && ![null, "ExtraParts"].includes(this.claimDto.ClaimType);
    },
    ShowHideDataTable: function () {
      return this.searchData.length > 0;
    },
  },

  watch: {
    "claimDto.CustomerReferenceNumber": function (val) {
      if (val.length > 0) {
        this.$validator.validateAll().then((result) => {
          this.validationState.orderLookupTab = result;
        });
      }
    },
  },

  methods: {
    SearchByPartNumber: function () {
      this.loadingSearchByPartNumber = true;
      // if an invoice was selected and another search done this clears parent selected data
      // which is important to ensure proper validation when clicking 'Submit'
      this.$emit("selectedInvoice", { invoiceId: Number, invoiceSource: String });
      this.searchText = this.partNumber;
      this.LoadInvoiceData(true).then((invoiceDto) => {
        this.searchData = invoiceDto.data;
        this.FilterIscalaInvoices();
        this.SetClaimAllowedDaysMessage();
        this.loadingSearchByPartNumber = false;
      });
    },
    SearchByInvoiceOrderPo: function () {
      // if an invoice was selected and another search done this clears parent selected data
      // which is important to ensure proper validation when clicking 'Submit'
      this.loadingSearchByInvoice = true;
      this.$emit("selectedInvoice", { invoiceId: Number, invoiceSource: String });
      this.searchText = this.invoiceOrderNumber;
      this.LoadInvoiceData(false).then((invoiceDto) => {
        this.searchData = invoiceDto.data;
        this.FilterIscalaInvoices();
        this.SetClaimAllowedDaysMessage();
        this.loadingSearchByInvoice = false;
      });
    },
    SetSelectedRow(selectedRow) {
      this.$emit("selectedInvoice", {
        invoiceId: selectedRow.InvoiceId,
        invoiceSource: selectedRow.InvoiceSource,
        orderNumber: selectedRow.OrderNumber,
        salesOrg: selectedRow.SalesOrg,
        invoiceDate: selectedRow.InvoiceDate,
        invoiceNumber: selectedRow.InvoiceNumber,
        invoiceFreight: selectedRow.SubTotalFreight,
        SourceInvoiceNumber: selectedRow.SourceInvoiceNumber,
        SourceCode: selectedRow.SourceCode,
      });
    },
    LoadInvoiceData(isPartSearch) {
      return ClaimsService.SearchInvoices(
        this.activeAccountId,
        this.user.UserId,
        this.searchText,
        isPartSearch,
        this.hasPermission("IgnoreReturnValidation")
      );
    },
    SetClaimAllowedDaysMessage() {
      if (this.searchData.length > 0) {
        let claimAllowedDays = this.searchData[0].ClaimAllowedDays;
        this.claimAllowedDaysMessage = `Claims are only valid ${claimAllowedDays} days from the invoice date.`;
      }
    },

    FilterIscalaInvoices() {
      this.iScalaInvoices = this.searchData
        .filter((x) => x.SourceCode === "ISC")
        .map((y) => y.InvoiceNumber)
        .join(", ");
      this.searchData = this.searchData.filter((x) => x.SourceCode !== "ISC");
    },
  },
};
</script>
